import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUserData } from '../components/UserDataContext';
import { FeedHideService } from '../services/FeedHideService';
import { getOriginalYapId } from '../components/utils/yapUtils';

/**
 * Loads this reader's "recently interacted" set for the World Yap tab. The feed
 * uses it to demote those yaps a bounded number of places so untouched ones take
 * priority; nothing is removed, and nothing is pushed out of easy reach.
 *
 * The set is fetched once per feed load rather than kept live on purpose. If it
 * updated the instant an interaction was recorded, a yap would jump away from
 * under the finger that just liked it. Holding the set still until the next load
 * means the reader finishes with the post they engaged with, and it sits further
 * down next time they come back.
 *
 * @param reloadKey bump this (the feed's lastRefreshTime) to re-query.
 */
export function useFeedHides(reloadKey?: number) {
    const { userProfile } = useUserData();
    const uid = String(userProfile?.id || '');

    const [hides, setHides] = useState<Map<string, number>>(() => new Map());

    // A logged-out visitor has no hides, and the query would be denied anyway.
    useEffect(() => {
        let cancelled = false;
        if (!uid) {
            setHides(new Map());
            return;
        }
        FeedHideService.fetchActiveHides().then((next) => {
            if (!cancelled) setHides(next);
        });
        return () => { cancelled = true; };
    }, [uid, reloadKey]);

    /**
     * True when this feed item should give up its place to untouched yaps.
     *
     * Never hides the reader's own content: burying your own yap because you
     * replied to your own thread is not what "show me something new" means. A
     * quote shout carries the original author under `userId` and the shouter
     * under `shoutedBy`, so both are checked.
     */
    const isFeedHidden = useCallback(
        (item: any): boolean => {
            if (!item || hides.size === 0) return false;
            if (uid && (String(item.userId || '') === uid || String(item.shoutedBy || '') === uid)) {
                return false;
            }
            return hides.has(getOriginalYapId(item));
        },
        [hides, uid],
    );

    /** When the interaction happened (ms), or 0. Drives the Trending floor's ordering. */
    const hiddenAt = useCallback(
        (item: any): number => hides.get(getOriginalYapId(item)) || 0,
        [hides],
    );

    return useMemo(
        () => ({ feedHides: hides, isFeedHidden, hiddenAt }),
        [hides, isFeedHidden, hiddenAt],
    );
}
